import {
    type AnyRouter,
    type Constrain,
    type InferFrom,
    type InferMaskFrom,
    type InferMaskTo,
    type InferTo,
    type LinkComponentProps,
    type RegisteredRouter,
    useRouter,
} from '@tanstack/react-router';
import * as React from 'react';

import type { AppLinkOptions, LinkBrand } from './linkOptions';
import { getMountedRouter } from './mountedRouter';

function isAbsoluteUrl(to: unknown): to is string {
    if (typeof to !== 'string') {
        return false;
    }
    try {
        return Boolean(new URL(to));
    } catch {
        return false;
    }
}

/** `ValidateLinkOptions` with the constraint behind `NoInfer`: inferring into it cost a second of tsc per call. */
type ValidateLinkOptionsNoInfer<T> = Constrain<
    T,
    NoInfer<LinkComponentProps<'a', RegisteredRouter, InferFrom<T>, InferTo<T>, InferMaskFrom<T>, InferMaskTo<T>>>
>;

/** Internal: `buildHref` against a given router. Not exported from any barrel. */
export function buildHrefWith(router: AnyRouter, options: unknown, config?: { absolute?: boolean }): string {
    const to = (options as { to?: unknown }).to;
    if (isAbsoluteUrl(to)) {
        throw new Error(`buildHref takes app routes; got an absolute URL: ${to}`);
    }
    const { href } = router.buildLocation(options as never);
    return config?.absolute ? window.location.origin + href : href;
}

/** The URL for typed link options; resolves against the mounted router, so call it from a handler or effect. */
export function buildHref<const T>(
    // A conditional, not a union: with the unknown search, an undeclared key's error would move to the whole object.
    options: [T] extends [LinkBrand] ? AppLinkOptions : ValidateLinkOptionsNoInfer<T>,
    config?: { absolute?: boolean },
): string {
    const router = getMountedRouter();
    if (!router) {
        throw new Error('buildHref needs a mounted router');
    }
    return buildHrefWith(router, options, config);
}

/** `buildHref` for render, via the context router (there on first render); the returned function throws without one. */
export function useBuildHref(): (options: AppLinkOptions, config?: { absolute?: boolean }) => string {
    const router = useRouter({ warn: false }) as AnyRouter | null | undefined;
    return React.useCallback(
        (options, config) => {
            if (!router) {
                throw new Error('useBuildHref needs a router');
            }
            return buildHrefWith(router, options, config);
        },
        [router],
    );
}
